import React, { useState, useEffect, useRef } from 'react';
import { askQuestion, getQuestions, getSuggestedQuestions, getQuestionUsage } from '../utils/api';
import { Link } from 'react-router-dom';

const ContractQA = ({ analysisId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [conversations, setConversations] = useState([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadQuestions();
      loadSuggestions();
      loadUsage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, analysisId]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversations]);

  const loadQuestions = async () => {
    try {
      const response = await getQuestions(analysisId);
      setConversations(response.data.questions || []);
    } catch (err) {
      console.error('Failed to load questions:', err);
    }
  };

  const loadSuggestions = async () => {
    try {
      const response = await getSuggestedQuestions(analysisId);
      setSuggestedQuestions(response.data.suggestions || []);
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    }
  };

  const loadUsage = async () => {
    try {
      const response = await getQuestionUsage();
      setUsage(response.data);
    } catch (err) {
      console.error('Failed to load usage:', err);
    }
  };

  const handleAskQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim() || loading) return;

    const userQuestion = question.trim();
    setQuestion('');
    setError('');
    setLoading(true);

    // Optimistically add user's question to UI
    const tempId = Date.now();
    setConversations(prev => [...prev, {
      id: tempId,
      question: userQuestion,
      answer: null,
      created_at: new Date().toISOString()
    }]);

    try {
      const response = await askQuestion(analysisId, userQuestion);

      // Replace temporary entry with actual response
      setConversations(prev => prev.map(conv =>
        conv.id === tempId
          ? { ...conv, answer: response.data.answer }
          : conv
      ));

      // Update usage
      setUsage(prev => ({
        ...prev,
        questions_used: response.data.questions_used,
        questions_remaining: response.data.questions_remaining
      }));

    } catch (err) {
      console.error('Failed to ask question:', err);

      // Remove temporary entry on error
      setConversations(prev => prev.filter(conv => conv.id !== tempId));

      if (err.response?.status === 402) {
        setError(err.response.data.message || 'Question limit reached. Please upgrade your plan.');
      } else {
        setError('Failed to get answer. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestedQuestion = (suggested) => {
    setQuestion(suggested);
  };

  if (!usage?.has_access) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-12 w-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-4 flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Ask Questions About Your Contract</h3>
            <p className="text-gray-600 mb-4">
              Get instant answers to specific questions about clauses, terms, and risks in your contract.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Upgrade to ask questions:</strong> Monthly plan includes 20 questions/month, Pro plan includes 60 questions/month.
              </p>
            </div>
            <Link
              to="/pricing"
              className="inline-block bg-primary-600 text-white hover:bg-primary-700 px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              View Plans & Pricing
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg mb-8">
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center">
          <svg className="h-8 w-8 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-left">
            <h3 className="text-xl font-bold text-gray-900">Ask Questions</h3>
            <p className="text-sm text-gray-600">Get instant answers about your contract</p>
          </div>
        </div>
        <div className="flex items-center">
          {usage && (
            <span className="text-sm text-gray-600 mr-4">
              {usage.questions_remaining === 'unlimited'
                ? 'Unlimited questions'
                : `${usage.questions_remaining} questions remaining`
              }
            </span>
          )}
          <svg
            className={`h-6 w-6 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Chat Interface */}
      {isOpen && (
        <div className="border-t border-gray-200 p-6">
          {/* Conversation History */}
          <div className="mb-4 max-h-96 overflow-y-auto space-y-4">
            {conversations.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">No questions asked yet. Try one of the suggested questions below!</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <div key={conv.id} className="space-y-3">
                  {/* User Question */}
                  <div className="flex justify-end">
                    <div className="bg-primary-600 text-white rounded-lg px-4 py-3 max-w-2xl">
                      <p className="text-sm font-medium mb-1">You asked:</p>
                      <p>{conv.question}</p>
                    </div>
                  </div>

                  {/* AI Answer */}
                  {conv.answer ? (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 text-gray-900 rounded-lg px-4 py-3 max-w-2xl">
                        <p className="text-sm font-medium mb-1 text-primary-600">Answer:</p>
                        <div className="prose prose-sm max-w-none">
                          {conv.answer.split('\n').map((paragraph, idx) => (
                            <p key={idx} className="mb-2 last:mb-0">{paragraph}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 text-gray-600 rounded-lg px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-sm">Analysing your question...</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Suggested Questions */}
          {suggestedQuestions.length > 0 && conversations.length === 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Suggested questions:</p>
              <div className="space-y-2">
                {suggestedQuestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestedQuestion(suggestion)}
                    className="block w-full text-left px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
              {error.includes('limit') && (
                <Link to="/pricing" className="text-sm text-red-900 font-semibold hover:underline">
                  Upgrade your plan →
                </Link>
              )}
            </div>
          )}

          {/* Question Input Form */}
          <form onSubmit={handleAskQuestion} className="flex gap-2">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask a question about this contract..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={loading}
              maxLength={500}
              minLength={10}
            />
            <button
              type="submit"
              disabled={loading || !question.trim() || question.trim().length < 10}
              className="bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                'Ask'
              )}
            </button>
          </form>
          <p className="text-xs text-gray-500 mt-2">
            Questions must be between 10-500 characters. {usage?.questions_remaining !== 'unlimited' && `${usage?.questions_remaining || 0} questions remaining this month.`}
          </p>
        </div>
      )}
    </div>
  );
};

export default ContractQA;
